import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { devicesService } from '../services/devices';
import { deviceSessionStore } from '../services/deviceSession';
import { useAuth } from '../hooks/useAuth';

type Status = 'loading' | 'success' | 'error';

// How long the confirmation stays up before we take the user on to the gym.
const REDIRECT_SECONDS = 10;

/**
 * Landing page for a device's auth link
 * (/gyms/:gymSlug/devices/:code/auth/:otp).
 *
 * Anyone who opened the link — signed in or not — is exchanging the device's
 * current OTP for a short-lived pass. On success the pass is stored locally and
 * used from then on; the page just reports the outcome.
 */
export default function DeviceAuthPage() {
  const { t } = useTranslation();
  const { gymSlug, code, otp } = useParams<{ gymSlug: string; code: string; otp: string }>();
  const { isAuthenticated, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState<Status>('loading');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);

  // React runs effects twice in dev StrictMode; the POST is harmless to repeat
  // but there is no reason to, and it keeps the outcome deterministic.
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;

    if (!gymSlug || !code || !otp) {
      setStatus('error');
      return;
    }

    // Authorizing grants the boards feature to a logged-in user; re-fetch the
    // profile so the gym's board tabs are visible by the time we take them on to
    // the gym (whether we just authorized or are honouring an existing pass).
    const markSuccess = (exp: string) => {
      setExpiresAt(exp);
      setStatus('success');
      if (isAuthenticated) {
        refreshProfile().catch(() => {});
      }
    };

    // The OTP is single-use: it is spent the moment the first authorization
    // succeeds. So a reload — or the service worker taking over and refreshing
    // the page — must not authorize again, or it would fail on the now-burned
    // code. If we already hold a valid pass for this device, honour it.
    const existing = deviceSessionStore.get(code);
    if (existing) {
      markSuccess(existing.expires_at);
      return;
    }

    devicesService
      .authorize(gymSlug, code, otp)
      .then((session) => {
        deviceSessionStore.save(code, session);
        markSuccess(session.expires_at);
      })
      .catch((error) => {
        setErrorMessage(error instanceof Error ? error.message : null);
        setStatus('error');
      });
  }, [gymSlug, code, otp, isAuthenticated, refreshProfile]);

  // Once authorized, take the user to the gym automatically after a short delay,
  // giving them a few seconds to read the confirmation. The profile refresh above
  // has already run, so the gym page reflects any newly unlocked board tabs. The
  // single redirect timeout is decoupled from the interval that ticks the visible
  // countdown, so the redirect fires exactly once.
  useEffect(() => {
    if (status !== 'success' || !gymSlug) return;
    const redirect = setTimeout(() => navigate(`/gyms/${gymSlug}`), REDIRECT_SECONDS * 1000);
    const tick = setInterval(() => setCountdown((n) => Math.max(0, n - 1)), 1000);
    return () => {
      clearTimeout(redirect);
      clearInterval(tick);
    };
  }, [status, gymSlug, navigate]);

  const expiryTime = expiresAt
    ? new Date(expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4">
      <Card className="w-full max-w-md">
        {status === 'loading' && (
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">{t('deviceAuth.authorizing')}</p>
          </CardContent>
        )}

        {status === 'success' && (
          <>
            <CardHeader className="items-center text-center">
              <CheckCircle2 className="mb-2 size-10 text-green-600" />
              <CardTitle>{t('deviceAuth.successTitle')}</CardTitle>
              <CardDescription>
                {t('deviceAuth.successDescription', { time: expiryTime })}
              </CardDescription>
            </CardHeader>
            {gymSlug && (
              <CardFooter className="flex flex-col items-center gap-2">
                <Button asChild>
                  <Link to={`/gyms/${gymSlug}`}>{t('deviceAuth.goToGym')}</Link>
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t('deviceAuth.redirecting', { seconds: countdown })}
                </p>
              </CardFooter>
            )}
          </>
        )}

        {status === 'error' && (
          <CardHeader className="items-center text-center">
            <XCircle className="mb-2 size-10 text-destructive" />
            <CardTitle>{t('deviceAuth.errorTitle')}</CardTitle>
            <CardDescription>
              {errorMessage || t('deviceAuth.errorDescription')}
            </CardDescription>
          </CardHeader>
        )}
      </Card>
    </div>
  );
}
